/**
 * The code a client types to confirm a sensitive step (S-27): adding another bank account,
 * requesting a withdrawal.
 *
 * Clients are never required to enrol in two-factor authentication, so a step that asks for a
 * code could be a dead end: the client typed everything, pressed Save, and was told to go and
 * turn it on elsewhere. Here the set-up is part of the step. A client who has no authenticator
 * is shown the enrolment (the same one the profile page has) and, once it is on, the code field
 * under it, without leaving the screen.
 */
import { useCallback, useEffect, useState } from 'react';
import authService from '../../services/auth.service';
import MfaEnrollmentSection from './MfaEnrollmentSection';
import { colors } from '../../styles/theme';

interface Props {
  readonly id: string;
  readonly value: string;
  readonly onChange: (code: string) => void;
  /** One line under the field: what the code confirms. */
  readonly hint: string;
}

export default function StepUpCodeField({ id, value, onChange, hint }: Props): JSX.Element {
  // null while the status is unknown; the field shows meanwhile, since most clients have it.
  const [enrolled, setEnrolled] = useState<boolean | null>(null);
  const [setUpHere, setSetUpHere] = useState(false);

  useEffect(() => {
    let cancelled = false;
    authService
      .getMfaStatus()
      .then((status) => {
        if (!cancelled) setEnrolled(status.enrolled);
      })
      .catch(() => {
        if (!cancelled) setEnrolled(null);
      });
    return () => { cancelled = true; };
  }, []);

  const onEnrolledChange = useCallback((now: boolean) => {
    setEnrolled(now);
  }, []);

  const showEnrolment = enrolled === false || setUpHere;

  return (
    <div>
      {showEnrolment && (
        <div className="mb-3">
          <p className="text-sm" style={{ color: colors.ink }}>
            First, set up your authenticator app. You&apos;ll use it to confirm this and every
            withdrawal.
          </p>
          <MfaEnrollmentSection
            onEnrolledChange={(now) => {
              if (!now) setSetUpHere(true);
              onEnrolledChange(now);
            }}
          />
        </div>
      )}
      {enrolled !== false && (
        <>
          <label htmlFor={id} className="block text-xs mb-1" style={{ color: colors.muted }}>
            Code from your authenticator app
          </label>
          <input
            id={id}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={value}
            onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 8))}
            placeholder="6-digit code"
            aria-describedby={`${id}-hint`}
            className="w-full px-3 py-2 rounded-lg text-sm font-mono tracking-widest"
            style={{ background: colors.surface, color: colors.ink,
                     border: `1px solid ${colors.controlBorder}` }}
          />
          <p id={`${id}-hint`} className="text-xs mt-1" style={{ color: colors.muted }}>{hint}</p>
        </>
      )}
    </div>
  );
}
